/**
 * Derive Operate mission type from existing backend classification fields.
 * Does not invent routes — only maps Mission Decision / RCA / mission view signals.
 */
import type { AmsIncident } from '../services/api';
import type { MissionDecision } from '../types/operations';
import type { ImplementationPlanResult } from './engineeringPlan';
import { rcaByType, strField } from './engineeringArtifacts';
import { extractOperationalRemediation } from './operationalRemediation';

/** Operator-facing mission families for Operate rendering. */
export type MissionType = 'ENGINEERING' | 'OPERATIONAL' | 'MANUAL' | 'UNCLASSIFIED';

export interface MissionClassification {
  mission_type: MissionType;
  /** Raw backend mission_route when present (operational_remediation | engineering_mission). */
  mission_route: string;
  /** HOW to resolve — from backend Resolution Strategy classifier. */
  resolution_strategy: string;
  failure_domain: string;
  mission_reason: string;
  suggested_action: string;
  outcome: string;
  invokes_engineering: boolean;
  /** Why the Engineering Planner is unavailable (empty when ENGINEERING). */
  planner_unavailable_reason: string;
  /** Confidence of the classification (0–1) from Mission Decision when available. */
  confidence: number | null;
  sources: string[];
}

function snapString(decision: MissionDecision | null | undefined, key: string): string {
  const snap = decision?.policy_snapshot;
  if (!snap || typeof snap !== 'object') return '';
  const v = (snap as Record<string, unknown>)[key];
  return typeof v === 'string' ? v.trim() : '';
}

/** Failure domain from Mission Decision snapshot or investigator_evidence bundle. */
export function extractFailureDomain(
  incident?: AmsIncident | null,
  decision?: MissionDecision | null,
): string {
  const fromSnap =
    snapString(decision, 'failure_domain_normalized') || snapString(decision, 'failure_domain');
  if (fromSnap) return fromSnap;

  const evidence = rcaByType(incident, 'investigator_evidence');
  const bundle = evidence?.bundle;
  if (bundle && typeof bundle === 'object') {
    const verdict = (bundle as Record<string, unknown>).investigation_verdict;
    const fd = strField(verdict, 'failure_domain');
    if (fd) return fd;
  }

  const report = rcaByType(incident, 'investigation_report');
  const fromReport = strField(report, 'failure_domain');
  if (fromReport) return fromReport;

  return '';
}

function outcomeOf(decision?: MissionDecision | null): string {
  return String(decision?.final_outcome || decision?.recommended_outcome || '').trim();
}

function routeOf(decision?: MissionDecision | null): string {
  return snapString(decision, 'mission_route');
}

/**
 * Classify the selected incident into ENGINEERING | OPERATIONAL | UNCLASSIFIED.
 * Prefers backend resolution_strategy / mission_type (HOW) over FailureDomain (WHERE).
 */
export function classifyMission(input: {
  incident?: AmsIncident | null;
  decision?: MissionDecision | null;
  planResult?: ImplementationPlanResult | null;
}): MissionClassification {
  const { incident, decision, planResult } = input;
  const sources: string[] = [];
  const failure_domain = extractFailureDomain(incident, decision);
  const mission_route = routeOf(decision);
  const outcome = outcomeOf(decision);
  const invokes_engineering = Boolean(decision?.invokes_engineering);
  const remediation = extractOperationalRemediation(incident);
  const missionStage = String(incident?.mission?.current_stage || '').toLowerCase();
  const resolution =
    String(
      incident?.resolution || incident?.mission?.resolution || incident?.operational_record?.resolution || '',
    );

  const resolution_strategy =
    String(decision?.resolution_strategy || snapString(decision, 'resolution_strategy') || '').trim();
  const backendMissionType = String(
    decision?.mission_type || snapString(decision, 'mission_type') || '',
  )
    .trim()
    .toUpperCase();
  const routing_reason =
    String(decision?.routing_reason || snapString(decision, 'routing_reason') || '').trim();

  const reason =
    routing_reason ||
    decision?.reasoning ||
    decision?.rationale ||
    snapString(decision, 'routing_note') ||
    snapString(decision, 'selected_by') ||
    '';

  const suggested_action = decision?.suggested_action || '';
  const confidence =
    typeof decision?.confidence === 'number' && Number.isFinite(decision.confidence)
      ? decision.confidence
      : null;

  // ── Preferred: backend Resolution Strategy → Mission Type ───────────
  if (backendMissionType === 'ENGINEERING' || resolution_strategy === 'ENGINEERING_PATCH') {
    sources.push(
      resolution_strategy
        ? `resolution_strategy=${resolution_strategy}`
        : `mission_type=${backendMissionType}`,
    );
    return done('ENGINEERING', {
      mission_route: mission_route || 'engineering_mission',
      resolution_strategy: resolution_strategy || 'ENGINEERING_PATCH',
      failure_domain,
      mission_reason: reason || 'Resolution strategy requires Engineering Planner.',
      suggested_action,
      outcome,
      invokes_engineering: true,
      planner_unavailable_reason: '',
      confidence,
      sources,
    });
  }
  if (backendMissionType === 'MANUAL' || resolution_strategy === 'MANUAL_ACTION' || outcome === 'escalate') {
    sources.push(
      resolution_strategy
        ? `resolution_strategy=${resolution_strategy}`
        : outcome === 'escalate'
          ? 'outcome=escalate'
          : `mission_type=${backendMissionType}`,
    );
    return done('MANUAL', {
      mission_route: mission_route || 'manual',
      resolution_strategy: resolution_strategy || 'MANUAL_ACTION',
      failure_domain,
      mission_reason:
        reason ||
        'Manual operator / business action — no autonomous operational recovery or engineering patch.',
      suggested_action: suggested_action || 'Follow runbook and escalate to the responsible owner',
      outcome: outcome || 'escalate',
      invokes_engineering: false,
      planner_unavailable_reason: plannerUnavailableReason(
        failure_domain,
        reason || 'MANUAL — operator guidance, not Engineering Planner or ops executor.',
        'MANUAL_ACTION',
      ),
      confidence,
      sources,
    });
  }
  if (backendMissionType === 'OPERATIONAL' || isOperationalStrategy(resolution_strategy)) {
    sources.push(
      resolution_strategy
        ? `resolution_strategy=${resolution_strategy}`
        : `mission_type=${backendMissionType}`,
    );
    return done('OPERATIONAL', {
      mission_route: mission_route || 'operational_remediation',
      resolution_strategy: resolution_strategy || 'DATA_REPAIR',
      failure_domain,
      mission_reason:
        reason ||
        `ResolutionStrategy ${resolution_strategy || 'operational'} — Engineering Planner does not apply.`,
      suggested_action: suggested_action || 'Execute operational remediation',
      outcome: outcome || 'operational_remediation',
      invokes_engineering: false,
      planner_unavailable_reason: plannerUnavailableReason(
        failure_domain,
        reason,
        resolution_strategy || mission_route,
      ),
      confidence,
      sources,
    });
  }
  if (resolution_strategy === 'UNKNOWN' && invokes_engineering) {
    sources.push('resolution_strategy=UNKNOWN → planner fallback');
    return done('ENGINEERING', {
      mission_route: mission_route || 'engineering_mission',
      resolution_strategy: 'UNKNOWN',
      failure_domain,
      mission_reason: reason || 'UNKNOWN strategy — Engineering Planner fallback.',
      suggested_action,
      outcome,
      invokes_engineering: true,
      planner_unavailable_reason: '',
      confidence,
      sources,
    });
  }

  // ── Legacy fallbacks (pre-strategy decisions) ───────────────────────
  if (mission_route === 'engineering_mission') {
    sources.push('policy_snapshot.mission_route=engineering_mission');
    return done('ENGINEERING', {
      mission_route,
      resolution_strategy: resolution_strategy || '',
      failure_domain,
      mission_reason: reason || 'Mission Decision selected Engineering Mission.',
      suggested_action,
      outcome,
      invokes_engineering: true,
      planner_unavailable_reason: '',
      confidence,
      sources,
    });
  }
  if (outcome === 'engineering_mission_required' || invokes_engineering) {
    sources.push(outcome === 'engineering_mission_required' ? 'outcome=engineering_mission_required' : 'invokes_engineering');
    return done('ENGINEERING', {
      mission_route: mission_route || 'engineering_mission',
      resolution_strategy: resolution_strategy || '',
      failure_domain,
      mission_reason: reason || 'Mission Decision invokes an Engineering Mission.',
      suggested_action,
      outcome,
      invokes_engineering: true,
      planner_unavailable_reason: '',
      confidence,
      sources,
    });
  }
  if (missionStage === 'engineering_mission' || incident?.mission?.assigned_engineering_mission) {
    sources.push('mission.current_stage / assigned_engineering_mission');
    return done('ENGINEERING', {
      mission_route: mission_route || 'engineering_mission',
      resolution_strategy: resolution_strategy || '',
      failure_domain,
      mission_reason: reason || 'Operational mission view is on the engineering stage.',
      suggested_action,
      outcome,
      invokes_engineering: true,
      planner_unavailable_reason: '',
      confidence,
      sources,
    });
  }

  if (mission_route === 'operational_remediation') {
    sources.push('policy_snapshot.mission_route=operational_remediation');
    return done('OPERATIONAL', {
      mission_route,
      resolution_strategy: resolution_strategy || '',
      failure_domain,
      mission_reason: reason || 'Mission Decision selected Operational Remediation.',
      suggested_action: suggested_action || 'Execute operational remediation (no engineering mission)',
      outcome: outcome || 'operational_remediation',
      invokes_engineering: false,
      planner_unavailable_reason: plannerUnavailableReason(failure_domain, reason, mission_route),
      confidence,
      sources,
    });
  }
  if (outcome === 'operational_remediation' || outcome === 'configuration_change') {
    sources.push(`outcome=${outcome}`);
    return done('OPERATIONAL', {
      mission_route: mission_route || 'operational_remediation',
      resolution_strategy: resolution_strategy || '',
      failure_domain,
      mission_reason: reason || `Mission Decision outcome is ${outcome.replace(/_/g, ' ')}.`,
      suggested_action,
      outcome,
      invokes_engineering: false,
      planner_unavailable_reason: plannerUnavailableReason(failure_domain, reason, mission_route || outcome),
      confidence,
      sources,
    });
  }
  if (
    planResult?.status === 'mission_route_operational' ||
    planResult?.blocking_gate === 'failure_domain_operational' ||
    planResult?.blocking_gate === 'mission_route_operational' ||
    planResult?.blocking_gate === 'resolution_strategy_operational'
  ) {
    sources.push(`implementation_plan.${planResult.blocking_gate || planResult.status}`);
    return done('OPERATIONAL', {
      mission_route: mission_route || 'operational_remediation',
      resolution_strategy: resolution_strategy || '',
      failure_domain: failure_domain || planResult.context_summary?.failure_domain || '',
      mission_reason: planResult.message || reason || 'Generate Plan gate reports Operational mission.',
      suggested_action,
      outcome: outcome || 'operational_remediation',
      invokes_engineering: false,
      planner_unavailable_reason:
        planResult.message ||
        plannerUnavailableReason(
          failure_domain || planResult.context_summary?.failure_domain || '',
          reason,
          'operational_remediation',
        ),
      confidence,
      sources,
    });
  }
  if (
    remediation.found ||
    resolution === 'operational_remediation_resolved' ||
    missionStage === 'operational_remediation'
  ) {
    sources.push(remediation.found ? 'rca.operational_remediation' : `mission_stage/resolution=${missionStage || resolution}`);
    return done('OPERATIONAL', {
      mission_route: mission_route || 'operational_remediation',
      resolution_strategy: resolution_strategy || '',
      failure_domain,
      mission_reason: reason || remediation.summary || 'Operational remediation artifacts present.',
      suggested_action,
      outcome: outcome || 'operational_remediation',
      invokes_engineering: false,
      planner_unavailable_reason: plannerUnavailableReason(failure_domain, reason, 'operational_remediation'),
      confidence,
      sources,
    });
  }

  // Do NOT assume FailureDomain alone ⇒ OPERATIONAL (may still need ENGINEERING_PATCH).
  if (isEngineeringFailureDomain(failure_domain)) {
    sources.push(`failure_domain=${failure_domain}`);
    return done('ENGINEERING', {
      mission_route: mission_route || 'engineering_mission',
      resolution_strategy: resolution_strategy || '',
      failure_domain,
      mission_reason: reason || `FailureDomain "${failure_domain}" suggests Engineering Mission pending strategy.`,
      suggested_action,
      outcome,
      invokes_engineering: true,
      planner_unavailable_reason: '',
      confidence,
      sources,
    });
  }

  sources.push('unclassified');
  return done('UNCLASSIFIED', {
    mission_route,
    resolution_strategy: resolution_strategy || '',
    failure_domain,
    mission_reason: reason || 'Mission classification is not yet available from the backend.',
    suggested_action,
    outcome,
    invokes_engineering,
    planner_unavailable_reason: '',
    confidence,
    sources,
  });
}

function isOperationalStrategy(s: string): boolean {
  return [
    'CONFIGURATION_CHANGE',
    'SERVICE_RESTART',
    'ROLLBACK',
    'SCALE_OPERATION',
    'DATA_REPAIR',
  ].includes(s);
}

function done(
  mission_type: MissionType,
  rest: Omit<MissionClassification, 'mission_type'>,
): MissionClassification {
  return { mission_type, ...rest };
}

function plannerUnavailableReason(domain: string, reason: string, route: string): string {
  const parts: string[] = [];
  parts.push(
    'This incident is classified by Resolution Strategy for Operational Remediation — not an Engineering Patch.',
  );
  if (route) {
    parts.push(`Resolution / route: ${route.replace(/_/g, ' ')}.`);
  }
  if (domain) {
    parts.push(
      `Failure domain "${domain}" describes WHERE the symptom appeared; it does not alone block or require the Engineering Planner.`,
    );
  }
  if (reason) {
    parts.push(reason);
  }
  parts.push(
    'Generate Plan, code diff, and patch approval apply only when ResolutionStrategy=ENGINEERING_PATCH.',
  );
  return parts.join(' ');
}

/** Canonical operational domains from backend failure_domain_policy.go */
export function isOperationalFailureDomain(raw: string): boolean {
  const d = normalizeDomain(raw);
  if (!d) return false;
  const ops = new Set([
    'configuration',
    'deployment',
    'infrastructure',
    'authentication',
    'network',
  ]);
  if (ops.has(d)) return true;
  return d.includes('data_quality');
}

export function isEngineeringFailureDomain(raw: string): boolean {
  const d = normalizeDomain(raw);
  if (!d) return false;
  const eng = new Set([
    'application_code',
    'compile_failure',
    'dependency',
    'runtime_bug',
    'validation_logic',
    'parser_bug',
    'ingestion_bug',
    'transformation_bug',
  ]);
  return eng.has(d);
}

function normalizeDomain(raw: string): string {
  return raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/-/g, '_');
}

export function missionTypeLabel(t: MissionType): string {
  switch (t) {
    case 'ENGINEERING':
      return 'Engineering Mission';
    case 'OPERATIONAL':
      return 'Operational Remediation';
    case 'MANUAL':
      return 'Manual / Escalation';
    default:
      return 'Mission unclassified';
  }
}
